import { useCallback, useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, IdentificationBadge } from "@phosphor-icons/react";

/* v2.93 · Admin permission grid. Only full admins see this page.
   Sub-admins can be granted specific capabilities:
     approve_pe_profiles, manage_jobs, manage_users, view_analytics, edit_content
   Full admins always have all capabilities (grid shows read-only). */

export default function AdminPermissionsPage() {
  const [users, setUsers] = useState([]);
  const [caps, setCaps] = useState([]);
  const [saving, setSaving] = useState("");
  const [addEmail, setAddEmail] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/permissions");
      setUsers(data.users); setCaps(data.known_capabilities);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u, cap) => {
    if (u.is_full_admin) { toast.info("Full admins always have all capabilities."); return; }
    const next = u.capabilities.includes(cap)
      ? u.capabilities.filter((c) => c !== cap)
      : [...u.capabilities, cap];
    setSaving(u.id);
    try {
      await api.put(`/admin/permissions/${u.id}`, { capabilities: next });
      await load();
      toast.success(`Updated ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(""); }
  };

  const addUser = async () => {
    if (!addEmail.trim()) return;
    // Look up the user by email to get their id — we reuse /admin/users if
    // available, else the user must already be visible in the grid.
    try {
      const { data } = await api.get("/admin/users").catch(() => ({ data: { users: [] } }));
      const list = Array.isArray(data) ? data : (data?.users || []);
      const target = addEmail.trim().toLowerCase();
      const found = list.find((u) => (u.email || "").toLowerCase() === target);
      if (!found) { toast.error("User not found — they must sign up first."); return; }
      await api.put(`/admin/permissions/${found.id}`, { capabilities: [] });
      setAddEmail("");
      await load();
      toast.success("User added to the permission grid.");
    } catch (e) {
      toast.error("Add failed");
    }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">Permission grid</h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Grant sub-admin capabilities without giving full-admin. Full admins always have every capability.
        </p>

        <div className="mt-6 border border-white/10 bg-[#12151C] p-3 flex items-center gap-2" data-testid="perm-add">
          <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
            placeholder="Add a user (email)…"
            className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="perm-add-email" />
          <button onClick={addUser} className="text-xs text-[#00E5FF] hover:underline font-mono" data-testid="perm-add-btn">
            + include user
          </button>
        </div>

        <div className="mt-6 border border-white/10 bg-[#12151C] overflow-x-auto">
          <table className="w-full text-sm font-mono" data-testid="perm-grid">
            <thead className="border-b border-white/10 text-xs text-gray-400 uppercase">
              <tr>
                <th className="text-left px-3 py-2">User</th>
                {caps.map((c) => <th key={c} className="text-center px-2 py-2 whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5" data-testid={`perm-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {u.is_full_admin
                        ? <ShieldCheck size={16} weight="fill" className="text-[#FFCC00]" />
                        : <IdentificationBadge size={16} weight="duotone" className="text-[#00E5FF]" />}
                      <div>
                        <div className="text-white">{u.name || u.email}</div>
                        <div className="text-[10px] text-gray-500">{u.email} · {u.role}</div>
                      </div>
                    </div>
                  </td>
                  {caps.map((c) => (
                    <td key={c} className="text-center px-2 py-2">
                      <input type="checkbox"
                        checked={u.capabilities.includes(c)}
                        disabled={u.is_full_admin || saving === u.id}
                        onChange={() => toggle(u, c)}
                        className="accent-[#00E5FF]"
                        data-testid={`perm-toggle-${u.id}-${c}`} />
                    </td>
                  ))}
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={caps.length + 1} className="px-3 py-6 text-center text-xs text-gray-500">
                  No users in the grid yet. Add someone above to grant capabilities.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
